"use client";

import { useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { AlertCircle, Loader2 } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Locale } from "@/i18n-config";
import { getAppCopy } from "@/content/app-copy";
import { getAuthSocialCopy } from "@/content/auth-social-copy";
import { getOnboardingCopy } from "@/content/onboarding-copy";
import { getSignupContinuityCopy } from "@/content/signup-continuity-copy";
import { toSupportedLocale } from "@/lib/email-locale";
import { buildAuthCallbackUrl, resolveSafeNextPath } from "@/lib/auth/oauth";
import { SocialProviderIcon } from "@/components/auth/social-provider-icon";

type SignalKey = "friction" | "ai" | "intent";

const SIGNAL_KEYS: SignalKey[] = ["friction", "ai", "intent"];

export function SignupView({ lang }: { lang: Locale }) {
  const normalizedLang = toSupportedLocale(lang);
  const router = useRouter();
  const searchParams = useSearchParams();
  const authCopy = getAppCopy(normalizedLang).auth.signup;
  const socialCopy = getAuthSocialCopy(normalizedLang);
  const onboardingCopy = getOnboardingCopy(normalizedLang);
  const continuityCopy = getSignupContinuityCopy(normalizedLang);
  const supabase = createClient();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reflectiveAnswer, setReflectiveAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<"google" | "github" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const requestedNext = searchParams.get("next");
  const safeNextPath = resolveSafeNextPath(requestedNext, `/${normalizedLang}/onboarding`);

  const answers = useMemo(() => {
    const nextAnswers: Record<string, string> = {};
    SIGNAL_KEYS.forEach((key) => {
      const queryValue = searchParams.get(key);
      if (queryValue) {
        nextAnswers[key] = queryValue;
      }
    });

    return nextAnswers;
  }, [searchParams]);

  const signalItems = useMemo(() => {
    const labelFor = (stepId: SignalKey, fallback: string) => {
      const step = onboardingCopy.steps.find((item) => item.id === stepId);
      const selectedValue = answers[stepId];
      if (!selectedValue || selectedValue === "__skipped__") {
        return fallback;
      }
      const option = step?.options.find((item) => item.value === selectedValue);
      return option?.label ?? fallback;
    };

    return [
      {
        id: "blocker",
        label: continuityCopy.signalLabels.blocker,
        value: labelFor("friction", continuityCopy.signalFallbacks.blocker),
      },
      {
        id: "confusion",
        label: continuityCopy.signalLabels.confusion,
        value: labelFor("ai", continuityCopy.signalFallbacks.confusion),
      },
      {
        id: "outcome",
        label: continuityCopy.signalLabels.outcome,
        value: labelFor("intent", continuityCopy.signalFallbacks.outcome),
      },
    ];
  }, [answers, continuityCopy, onboardingCopy.steps]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const redirectUrl = `${window.location.origin}/auth/callback?next=${encodeURIComponent(safeNextPath)}`;
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          preferred_locale: normalizedLang,
          locale: normalizedLang,
          lang: normalizedLang,
          language: normalizedLang,
        },
      },
    });

    if (signUpError) {
      setError(signUpError.message);
      setIsLoading(false);
      return;
    }

    if (!signUpData.session) {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError || !signInData.session) {
        const reason = signInError?.message ?? "Email confirmation is required before login.";
        setError(`${reason} Complete email verification in Supabase, then sign in.`);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(false);
    router.replace(`/${normalizedLang}/onboarding`);
  };

  const handleOAuthSignup = async (provider: "google" | "github") => {
    setError(null);
    setOauthLoading(provider);

    const redirectTo = buildAuthCallbackUrl(window.location.origin, safeNextPath);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
      },
    });

    if (oauthError) {
      setError(oauthError.message);
      setOauthLoading(null);
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#f4efe3] px-4 pb-16 pt-28 md:pb-24">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_10%_12%,rgba(180,83,9,0.14),transparent_36%),radial-gradient(circle_at_84%_16%,rgba(15,118,110,0.14),transparent_36%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(29,38,34,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(29,38,34,0.05)_1px,transparent_1px)] bg-[size:26px_26px] [mask-image:radial-gradient(ellipse_88%_72%_at_50%_0%,#000_72%,transparent_100%)] opacity-25" />

      <div className="container relative max-w-6xl">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <aside className="rounded-3xl border border-[#d8cab2] bg-[#fffaf1] p-8 shadow-[0_12px_28px_rgba(34,47,42,0.08)]">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#7d5c30]">
              {continuityCopy.contextTitle}
            </p>
            <h1 className="mt-4 text-3xl font-bold text-[#1b2220] md:text-4xl">{continuityCopy.formTitle}</h1>
            <p className="mt-4 text-[#334740]">{continuityCopy.contextLead}</p>
            <p className="mt-2 text-sm text-[#566861]">{continuityCopy.contextNoLoss}</p>

            <div className="mt-6 grid gap-3">
              {signalItems.map((item) => (
                <div
                  key={item.id}
                  className="rounded-xl border border-[#d9ccb5] bg-[#fffdf8] px-4 py-3"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.11em] text-[#6f7d77]">
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm text-[#253a34]">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-2xl border border-[#0f766e]/20 bg-[#eaf8f5] p-5">
              <p className="text-sm font-semibold text-[#184840]">{continuityCopy.pathTitle}</p>
              <ul className="mt-3 space-y-2">
                {continuityCopy.pathSteps.map((step) => (
                  <li key={step} className="flex items-start gap-2 text-sm text-[#2f4e47]">
                    <span className="mt-[0.42rem] h-1.5 w-1.5 rounded-full bg-[#0f766e]" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="rounded-3xl border border-[#d8cab2] bg-[#fffaf1] p-8 shadow-[0_12px_28px_rgba(34,47,42,0.08)]">
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#7d5c30]">
                {continuityCopy.almostThere}
              </p>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#e7ddcc]">
                <div className="h-full w-[88%] rounded-full bg-[#0f766e]" />
              </div>
            </div>

            <p className="mb-6 text-[#3e5350]">{continuityCopy.formSubtitle}</p>

            <form onSubmit={handleSignup} className="space-y-5">
              {error ? (
                <div className="rounded-xl border border-[#c24f45]/25 bg-[#fff1ef] p-3 text-sm text-[#8b2f28]">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                  </div>
                </div>
              ) : null}

              <Input
                id="email"
                type="email"
                label={authCopy.emailLabel}
                placeholder={authCopy.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="border-[#d7c9b2] bg-[#fffdf8] text-[#1f2a27] placeholder:text-[#8ea099] focus-visible:ring-[#0f766e]"
              />

              <Input
                id="password"
                type="password"
                label={authCopy.passwordLabel}
                placeholder={authCopy.passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className="border-[#d7c9b2] bg-[#fffdf8] text-[#1f2a27] placeholder:text-[#8ea099] focus-visible:ring-[#0f766e]"
              />

              <Input
                id="build-first"
                type="text"
                label={continuityCopy.optionalQuestion}
                placeholder={continuityCopy.optionalPlaceholder}
                value={reflectiveAnswer}
                onChange={(e) => setReflectiveAnswer(e.target.value)}
                disabled={isLoading}
                className="border-[#d7c9b2] bg-[#fffdf8] text-[#1f2a27] placeholder:text-[#8ea099] focus-visible:ring-[#0f766e]"
              />

              <Button
                type="submit"
                disabled={isLoading || oauthLoading !== null}
                className="w-full border border-[#0f766e]/30 bg-[#0f766e] transition-transform duration-200 hover:bg-[#0e655e]"
                size="lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {continuityCopy.ctaLoading}
                  </>
                ) : (
                  continuityCopy.cta
                )}
              </Button>

              <div className="grid gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading || oauthLoading !== null}
                  onClick={() => void handleOAuthSignup("google")}
                  className="w-full border-[#d7c9b2] bg-[#fffdf8] text-[#1f2a27] hover:bg-[#f8f1e4]"
                >
                  <SocialProviderIcon provider="google" className="mr-2 h-4 w-4" />
                  {oauthLoading === "google" ? socialCopy.connecting : socialCopy.continueWithGoogle}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading || oauthLoading !== null}
                  onClick={() => void handleOAuthSignup("github")}
                  className="w-full border-[#d7c9b2] bg-[#fffdf8] text-[#1f2a27] hover:bg-[#f8f1e4]"
                >
                  <SocialProviderIcon provider="github" className="mr-2 h-4 w-4" />
                  {oauthLoading === "github" ? socialCopy.connecting : socialCopy.continueWithGithub}
                </Button>
              </div>

              <p className="text-center text-xs text-[#556a64]">{continuityCopy.reassurance}</p>

              <div className="text-center text-sm">
                <span className="text-[#5f726d]">{authCopy.hasAccount} </span>
                <Link
                  href={`/${lang}/login?next=${encodeURIComponent(safeNextPath)}`}
                  className="font-medium text-[#0f766e] hover:text-[#0d5d57]"
                >
                  {authCopy.login}
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
